import { useMutation } from '@tanstack/react-query'
import { App, Badge, Button, Form, Modal, Tabs, Tooltip } from 'antd'
import { Inbox, MessageCircleQuestionMark, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  supportTicketsCreateMutation,
  supportTicketsListQueryKey
} from 'src/api/main'
import { useInvalidatePredicateQueries } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

import { useTicketActivity } from '../../hooks'
import type { TicketFormState } from '../../types'
import { SubmittedTicketsList } from '../SubmittedTicketsList'
import { TicketForm } from '../TicketForm'

import { useStyles } from './styles'

const SUBMIT_TAB = 'submit'
const MY_TICKETS_TAB = 'my-tickets'

export const Tickets = () => {
  const { styles } = useStyles()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const showApiErrors = useShowApiErrors()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(SUBMIT_TAB)

  const [form] = Form.useForm<TicketFormState>()
  const { message } = App.useApp()
  const { t } = useTranslation()
  const { ticketsWithSupportReplyCount } = useTicketActivity()
  const badgeOffset: [number, number] =
    ticketsWithSupportReplyCount > 10 ? [0, 3] : [-2, 3]

  const onCancel = () => {
    form.resetFields()
    setActiveTab(SUBMIT_TAB)
    setIsModalOpen(false)
  }

  const { mutate: createTicket, isPending } = useMutation({
    ...supportTicketsCreateMutation(),
    onSuccess: () => {
      invalidateQueries([supportTicketsListQueryKey()])
      message.success(t('tickets.successCreate'))
      form.resetFields()
      setActiveTab(MY_TICKETS_TAB)
    },
    onError: (error) => showApiErrors(error, form)
  })

  const onSubmit = async () => {
    let values: TicketFormState

    try {
      values = await form.validateFields()
    } catch {
      return
    }

    createTicket({
      body: {
        title: values.title,
        description: values.description ?? '',
        priority: values.priority ?? null,
        source_url: window.location.href,
        attachment_ids: (values.attachments ?? []).flatMap((attachment) =>
          attachment.response ? [attachment.response.id] : []
        )
      }
    })
  }

  const onModalOpen = () => setIsModalOpen((prev) => !prev)

  const focusFirstField = (open: boolean) => {
    if (open && activeTab === SUBMIT_TAB) form.focusField('title')
  }

  return (
    <>
      <Tooltip title={t('tickets.create')}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            lineHeight: 1
          }}
        >
          <Badge
            count={ticketsWithSupportReplyCount}
            size="small"
            offset={badgeOffset}
          >
            <Button
              onClick={onModalOpen}
              type="text"
              icon={<MessageCircleQuestionMark size={18} />}
            />
          </Badge>
        </span>
      </Tooltip>

      <Modal
        open={isModalOpen}
        onCancel={onCancel}
        afterOpenChange={focusFirstField}
        footer={null}
        width={800}
        centered
      >
        <Tabs
          className={styles.tabs}
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: SUBMIT_TAB,
              icon: <Send size={16} strokeWidth={1.5} />,
              label: t('tickets.submitTab'),
              children: (
                <>
                  <TicketForm form={form} />
                  <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Button onClick={onCancel} style={{ marginRight: 8 }}>
                      {t('tickets.cancel')}
                    </Button>
                    <Button
                      type="primary"
                      loading={isPending}
                      onClick={onSubmit}
                    >
                      {t('tickets.submit')}
                    </Button>
                  </div>
                </>
              )
            },
            {
              key: MY_TICKETS_TAB,
              icon: <Inbox size={16} strokeWidth={1.5} />,
              label: (
                <Badge
                  count={ticketsWithSupportReplyCount}
                  size="small"
                  offset={[8, 6]}
                >
                  {t('tickets.myTicketsTab')}
                </Badge>
              ),
              children: <SubmittedTicketsList />
            }
          ]}
        />
      </Modal>
    </>
  )
}
